import { FC } from 'react'
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Link,
} from '@mui/material'
import {
  Work as WorkIcon,
  Visibility as ViewIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
  Done as DoneIcon,
  Replay as ReplayIcon,
} from '@mui/icons-material'
import { Link as RouterLink } from 'react-router-dom'
import { useGetProjectsQuery, useGetClientsQuery } from '../../redux/hooks.ts'
import type { ProjectModel } from '@itemize/server/models'

interface ProjectsTableProps {
  onView: (project: ProjectModel) => void
  onEdit: (project: ProjectModel) => void
  onDelete: (project: ProjectModel) => void
  onToggleStatus: (project: ProjectModel) => void
}
export interface ProjectsTableSectionProps extends ProjectsTableProps {
  title: string
  projects: ProjectModel[]
  clientMap: Record<string, string>
  defaultExpanded?: boolean
}

// Component to render a table section
const ProjectTableSection: FC<ProjectsTableSectionProps> = ({
  title,
  projects,
  clientMap,
  onView,
  onEdit,
  onDelete,
  onToggleStatus,
  defaultExpanded = true,
}) => {
  if (projects.length === 0) {
    return null
  }

  return (
    <Accordion
      defaultExpanded={defaultExpanded}
      sx={{
        '&:before': { display: 'none' },
        boxShadow: 'none',
        '&:not(:last-child)': {
          borderBottom: 0,
        },
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={{
          backgroundColor: 'grey.50',
          borderBottom: '1px solid',
          borderColor: 'divider',
          '&.Mui-expanded': {
            minHeight: 48,
          },
          '& .MuiAccordionSummary-content': {
            '&.Mui-expanded': {
              margin: '12px 0',
            },
          },
        }}
      >
        <Typography variant="h6">
          {title} ({projects.length})
        </Typography>
      </AccordionSummary>
      <AccordionDetails sx={{ padding: 0 }}>
        <TableContainer>
          <Table>
            <TableHead sx={{ display: 'none' }}>
              <TableRow>
                <TableCell>Client Name</TableCell>
                <TableCell>Project Name</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {projects.map((project: ProjectModel) => (
                <TableRow
                  key={project.id}
                  sx={{
                    '&:hover': {
                      backgroundColor: 'action.hover',
                    },
                  }}
                >
                  <TableCell>
                    <Link
                      component={RouterLink}
                      to={`/clients/${project.clientId}`}
                      sx={{
                        textDecoration: 'none',
                        '&:hover': {
                          textDecoration: 'underline',
                        },
                      }}
                    >
                      <Typography variant="body1">
                        {clientMap[project.clientId] || 'Unknown Client'}
                      </Typography>
                    </Link>
                  </TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Typography variant="body1">{project.name}</Typography>
                    </Box>
                  </TableCell>
                  <TableCell align="right">
                    <Box display="flex" gap={0.5} justifyContent="flex-end">
                      <Tooltip title="View">
                        <IconButton onClick={() => onView(project)} size="small">
                          <ViewIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={project.status === 'open' ? 'Mark as Done' : 'Mark as Open'}>
                        <IconButton onClick={() => onToggleStatus(project)} size="small">
                          {project.status === 'open' ? <DoneIcon /> : <ReplayIcon />}
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Edit">
                        <IconButton onClick={() => onEdit(project)} size="small">
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton onClick={() => onDelete(project)} size="small">
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </AccordionDetails>
    </Accordion>
  )
}

export const ProjectsTable: FC<ProjectsTableProps> = ({
  onView,
  onEdit,
  onDelete,
  onToggleStatus,
}) => {
  const { data: projects, isLoading, error } = useGetProjectsQuery()
  const { data: clients = [] } = useGetClientsQuery()

  // Create a map of client IDs to client names for quick lookup
  const clientMap = clients.reduce(
    (acc, client) => {
      acc[client.id] = client.name
      return acc
    },
    {} as Record<string, string>,
  )

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    )
  }

  if (error) {
    return <Alert severity="error">Error loading projects: {String(error)}</Alert>
  }

  if (!projects || projects.length === 0) {
    return (
      <Paper elevation={2} sx={{ padding: 4, textAlign: 'center' }}>
        <WorkIcon sx={{ fontSize: 64, color: 'text.secondary', marginBottom: 2 }} />
        <Typography variant="h6" color="text.secondary">
          No projects found
        </Typography>
        <Typography color="text.secondary">Start by creating your first project.</Typography>
      </Paper>
    )
  }

  // Separate projects into categories
  const openProjects = projects.filter((project) => project.status === 'open')
  const closedProjects = projects.filter((project) => project.status === 'closed')

  return (
    <Box>
      <Typography variant="body1" color="text.secondary" gutterBottom>
        {projects.length} project{projects.length !== 1 ? 's' : ''} found
      </Typography>

      <Paper elevation={2} sx={{ marginTop: 2, backgroundColor: 'transparent' }}>
        <ProjectTableSection
          title="Open Projects"
          projects={openProjects}
          clientMap={clientMap}
          onView={onView}
          onEdit={onEdit}
          onDelete={onDelete}
          onToggleStatus={onToggleStatus}
          defaultExpanded={true}
        />
      </Paper>
      <Paper elevation={2} sx={{ marginTop: 2, backgroundColor: 'transparent' }}>
        <ProjectTableSection
          title="Closed Projects"
          projects={closedProjects}
          clientMap={clientMap}
          onView={onView}
          onEdit={onEdit}
          onDelete={onDelete}
          onToggleStatus={onToggleStatus}
          defaultExpanded={false}
        />
      </Paper>
    </Box>
  )
}
